import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'
import { serviceAreas } from '@/constants/areas'

interface ServiceAreaMapProps {
    activeSlug?: string | null
    onActivate?: (slug: string | null) => void
    className?: string
}

const CENTER = 200

const RINGS = [
    { radius: 43.5, label: "1 h 30" },
    { radius: 87, label: "2 h" },
    { radius: 130.5, label: "2 h 30" },
    { radius: 174, label: "3 h" }
]

/**
 * Schematic dispatch map: every town sits at a distance from the Riverton yard
 * that matches its typical urgent arrival time.
 */
export const ServiceAreaMap: FC<ServiceAreaMapProps> = ({ activeSlug, onActivate, className }): ReactNode => {
    const ref = useRef<SVGSVGElement>(null)

    useGSAP(() => {
        const svg = ref.current

        if (!svg) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            gsap.timeline({ scrollTrigger: { trigger: svg, start: 'top 80%', once: true } })
                .from(svg.querySelectorAll('[data-ring]'), {
                    scale: 0.2,
                    autoAlpha: 0,
                    transformOrigin: '50% 50%',
                    duration: 1.4,
                    ease: 'expo.out',
                    stagger: 0.12
                })
                .from(svg.querySelectorAll('[data-spoke]'), {
                    strokeDashoffset: 1,
                    duration: 1.1,
                    ease: 'power3.inOut',
                    stagger: 0.06
                }, 0.35)
                .from(svg.querySelectorAll('[data-node]'), {
                    scale: 0,
                    transformOrigin: '50% 50%',
                    duration: 0.8,
                    ease: 'back.out(2)',
                    stagger: 0.06
                }, 0.8)
                .from(svg.querySelectorAll('[data-label]'), {
                    autoAlpha: 0,
                    y: 6,
                    duration: 0.6,
                    stagger: 0.05
                }, 1)
        })

        return () => media.revert()
    }, { scope: ref })

    return (
        <svg
            ref={ref}
            viewBox='0 0 400 400'
            role='img'
            aria-label="Map of usual urgent arrival times from our Riverton dispatch yard to all 8 towns we serve"
            className={cn('h-auto w-full overflow-visible', className)}
        >
            {RINGS.map(ring => (
                <g key={ring.radius} data-ring>
                    <circle
                        cx={CENTER}
                        cy={CENTER}
                        r={ring.radius}
                        fill='none'
                        stroke='currentColor'
                        strokeOpacity={0.16}
                        strokeDasharray='2 5'
                    />

                    <text
                        x={CENTER}
                        y={CENTER - ring.radius - 5}
                        textAnchor='middle'
                        className='fill-current font-mono text-[8.5px] uppercase tracking-[0.12em] opacity-40'
                    >
                        {ring.label}
                    </text>
                </g>
            ))}

            {serviceAreas.filter(area => area.slug !== 'riverton').map(area => (
                <line
                    key={`spoke-${area.slug}`}
                    data-spoke
                    x1={CENTER}
                    y1={CENTER}
                    x2={area.position.x}
                    y2={area.position.y}
                    pathLength={1}
                    strokeDasharray='1'
                    stroke='#8fb0d4'
                    strokeOpacity={activeSlug === area.slug ? 0.9 : 0.35}
                    strokeWidth={activeSlug === area.slug ? 1.6 : 1}
                    className='transition-[stroke-opacity,stroke-width] duration-300'
                />
            ))}

            {serviceAreas.map(area => {
                const isCenter = area.slug === 'riverton'
                const active = activeSlug === area.slug
                const labelLeft = area.position.x > 260
                const labelX = labelLeft ? area.position.x - 13 : area.position.x + 13

                return (
                    <g
                        key={area.slug}
                        onMouseEnter={() => onActivate?.(area.slug)}
                        onMouseLeave={() => onActivate?.(null)}
                        className='cursor-pointer'
                    >
                        {isCenter && (
                            <circle
                                cx={CENTER}
                                cy={CENTER}
                                r={11}
                                fill='#8fb0d4'
                                className='animate-live origin-center [transform-box:fill-box]'
                            />
                        )}

                        <g data-node>
                            <circle
                                cx={area.position.x}
                                cy={area.position.y}
                                r={isCenter ? 11 : active ? 9 : 7}
                                fill={isCenter || active ? '#8fb0d4' : 'currentColor'}
                                fillOpacity={isCenter || active ? 1 : 0.12}
                                stroke={isCenter || active ? '#8fb0d4' : 'currentColor'}
                                strokeOpacity={isCenter || active ? 1 : 0.5}
                                className='transition-all duration-300'
                            />

                            <circle
                                cx={area.position.x}
                                cy={area.position.y}
                                r={isCenter ? 4 : 2.6}
                                fill={isCenter || active ? '#ffffff' : 'currentColor'}
                            />
                        </g>

                        <g data-label>
                            <text
                                x={isCenter ? CENTER : labelX}
                                y={isCenter ? CENTER + 26 : area.position.y - 1}
                                textAnchor={isCenter ? 'middle' : labelLeft ? 'end' : 'start'}
                                className={cn('fill-current text-[11.5px] transition-opacity duration-300', active || isCenter ? 'font-semibold opacity-100' : 'opacity-80')}
                            >
                                {area.name}
                            </text>

                            <text
                                x={isCenter ? CENTER : labelX}
                                y={isCenter ? CENTER + 38 : area.position.y + 11}
                                textAnchor={isCenter ? 'middle' : labelLeft ? 'end' : 'start'}
                                className='fill-current font-mono text-[8.5px] opacity-50'
                            >
                                {isCenter ? "Our yard" : area.arrival}
                            </text>
                        </g>
                    </g>
                )
            })}
        </svg>
    )
}
